// @ts-strict-ignore
import { useQuery, useMutation } from '@tanstack/react-query';
import { useContext } from 'react';
import { UserContext } from '~/dashboard/context';
import apiCalls from '~/helpers/apiCalls';
import { ONE_MINUTE_IN_MS } from '~/helpers/constants/time';
import type { ThresholdResultsResponse, EvidenceResultsResponse } from './types';
import type { Dimension } from '~/application-review/redux/types';
import { pollDataExport } from '~/investigations/utils';
import initiateCSVDownload from '~/helpers/csv';

interface CommonUrlParams {
  entityToken: string;
  evaluationToken: string;
  isPortfolioEvaluation: boolean;
  dimensions?: Dimension[];
}

interface ThresholdResultsParams {
  canViewRules: boolean;
}

interface ThresholdEvidenceParams {
  actionId: number;
}
export const useThresholdResults = ({
  entityToken,
  evaluationToken,
  canViewRules,
  isPortfolioEvaluation,
  dimensions,
}: CommonUrlParams & ThresholdResultsParams) => {
  const { typhonDomain, featureFlags } = useContext(UserContext);

  return useQuery({
    queryKey: ['threshold-results', evaluationToken],
    queryFn: async () => {
      const result: ThresholdResultsResponse = await apiCalls.entities.getActionThresholdResults({
        typhonDomain,
        entityToken,
        evaluationToken,
        isPortfolioEvaluation,
        dimensions: (dimensions || []).map(({ dimension, dimension_type }) => ({
          dimension,
          dimension_type,
        })),
      });

      return result;
    },
    retry: false,
    staleTime: ONE_MINUTE_IN_MS * 10,
    enabled: (
      canViewRules
      && featureFlags?.ruleExplainabilityPhase > 0
      && Boolean(entityToken && evaluationToken)
    ),
  });
};

export const useActionEvidences = ({
  entityToken,
  evaluationToken,
  actionId,
  dimensions,
  isPortfolioEvaluation,
}: CommonUrlParams & ThresholdEvidenceParams) => {
  const { typhonDomain, featureFlags } = useContext(UserContext);

  return useQuery({
    queryKey: ['action-evidences', entityToken, evaluationToken, actionId, isPortfolioEvaluation, dimensions],
    queryFn: async () => {
      const result: EvidenceResultsResponse = await apiCalls.entities.getActionEvidence({
        typhonDomain,
        entityToken,
        evaluationToken,
        actionId,
        isPortfolioEvaluation,
        dimensions: (dimensions || []).map(({ dimension, dimension_type }) => ({
          dimension,
          dimension_type,
        })),
      });

      return result;
    },
    retry: false,
    staleTime: ONE_MINUTE_IN_MS * 10,
    enabled: (
      featureFlags?.ruleExplainabilityPhase > 0
      && Boolean(entityToken && evaluationToken && actionId)
    ),
  });
};

export const useThresholdEvidenceExportCsv = ({
  entityToken,
  evaluationToken,
  actionId,
  thresholdId,
  displayName,
  isPortfolioEvaluation,
  dimensions,
}) => {
  const { typhonDomain } = useContext(UserContext);

  return useMutation({
    mutationFn: async () => {
      const { export_token: exportToken } = await apiCalls.entities.exportThresholdEvidenceCsv({
        typhonDomain,
        entityToken,
        evaluationToken,
        actionId,
        thresholdId,
        isPortfolioEvaluation,
        dimensions: (dimensions || []).map(({ dimension, dimension_type }) => ({
          dimension,
          dimension_type,
        })),
      });

      const csvResponse = await pollDataExport({
        exportToken,
        typhonDomain,
      });
      const csvContent = await csvResponse.text();
      const csvDisplayName = displayName.toLowerCase().replace(/\s+/g, '-');
      initiateCSVDownload(`evidences-${evaluationToken}-${csvDisplayName}-${new Date().getTime()}.csv`, csvContent);
    },
  });
};
